import { Service } from '../types';
import { addService } from '../firebase/firestore';

const PENDING_SYNC_KEY = 'myMoney_pendingSync';
const LAST_SYNC_KEY = 'myMoney_lastSync';

interface PendingService {
  tempId: string;
  service: Omit<Service, 'id' | 'userId' | 'createdAt'>;
  timestamp: number;
}

// Adiciona serviço à fila de sincronização
export const addToPendingSync = (service: PendingService): void => {
  try {
    const pending = getPendingSync();
    pending.push(service);
    localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(pending));
  } catch (error) {
    console.error('Erro ao adicionar à fila:', error);
  }
};

// Obtém serviços pendentes
export const getPendingSync = (): PendingService[] => {
  try {
    const data = localStorage.getItem(PENDING_SYNC_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Erro ao ler fila de sincronização:', error);
    return [];
  }
};

// Remove serviço da fila
export const removeFromPendingSync = (tempId: string): void => {
  try {
    const pending = getPendingSync();
    const filtered = pending.filter(item => item.tempId !== tempId);
    localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Erro ao remover da fila:', error);
  }
};

// Sincroniza todos os serviços pendentes
export const syncPendingServices = async (userId: string): Promise<number> => {
  const pending = getPendingSync();
  
  if (pending.length === 0) {
    return 0;
  }

  
  let syncedCount = 0;
  const failedItems: PendingService[] = [];

  for (const item of pending) {
    try {
      await addService(userId, item.service);
      syncedCount++;
    } catch (error) {
      console.error(`❌ Falha ao sincronizar: ${item.service.description}`, error);
      failedItems.push(item);
    }
  }

  // Mantém apenas os itens que falharam
  localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(failedItems));
  
  // Atualiza timestamp da última sincronização
  localStorage.setItem(LAST_SYNC_KEY, Date.now().toString());

  
  return syncedCount;
};

// Verifica se deve sincronizar (executado quando online)
export const shouldSync = (): boolean => {
  const lastSync = localStorage.getItem(LAST_SYNC_KEY);
  const pending = getPendingSync();
  
  if (pending.length === 0) return false;
  
  if (!lastSync) return true;
  
  const timeSinceLastSync = Date.now() - parseInt(lastSync);
  const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutos
  
  return timeSinceLastSync > SYNC_INTERVAL;
};

// Hook para auto-sync quando online
export const initAutoSync = (userId: string | null): (() => void) | undefined => {
  if (!userId) return;

  // Sincroniza imediatamente se houver pendências
  if (shouldSync()) {
    syncPendingServices(userId).catch(console.error);
  }

  // Listener para quando voltar online
  const handleOnline = () => {
    syncPendingServices(userId).catch(console.error);
  };

  window.addEventListener('online', handleOnline);

  // Sync periódico a cada 5 minutos (se houver pendências)
  const syncInterval = setInterval(() => {
    if (navigator.onLine && shouldSync()) {
      syncPendingServices(userId).catch(console.error);
    }
  }, 5 * 60 * 1000);

  // Cleanup
  return () => {
    window.removeEventListener('online', handleOnline);
    clearInterval(syncInterval);
  };
};

// Salva serviços em cache local
const SERVICES_CACHE_KEY = 'myMoney_servicesCache';

export const cacheServices = (userId: string, services: Service[]): void => {
  try {
    const cache = {
      userId,
      services,
      timestamp: Date.now()
    };
    localStorage.setItem(SERVICES_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('Erro ao cachear serviços:', error);
  }
};

export const getCachedServices = (userId: string): Service[] | null => {
  try {
    const cached = localStorage.getItem(SERVICES_CACHE_KEY);
    if (!cached) return null;

    const { userId: cachedUserId, services, timestamp } = JSON.parse(cached);
    
    // Verifica se é do mesmo usuário
    if (cachedUserId !== userId) return null;

    // Cache válido por 10 minutos
    const CACHE_VALIDITY = 10 * 60 * 1000;
    if (Date.now() - timestamp > CACHE_VALIDITY) {
      localStorage.removeItem(SERVICES_CACHE_KEY);
      return null;
    }

    return services;
  } catch (error) {
    console.error('Erro ao ler cache de serviços:', error);
    return null;
  }
};
